/**
 * CKEditor Comprehensive Enhancement & Fix Script
 * Combines all CKEditor functionality into one optimized script:
 * - Modal dropdown z-index fixes
 * - Dropdown closing behavior
 * - Enhanced features (clipboard, fullscreen, etc.)
 * - Global enhancements
 */

(function() {
    'use strict';

    // ============================================================================
    // SECTION 1: MODAL DROPDOWN Z-INDEX FIX (Current Working Functionality)
    // ============================================================================
    
    function fixModalDropdowns() {
        const isModalOpen = document.body.classList.contains('modal-open');
        
        if (isModalOpen) {
            // STEP 1: Lower CK editor z-index to prevent stacking context issues
            const ckEditors = document.querySelectorAll('.modal .ck-editor, .modal .ck-editor__main, .modal .ck-content');
            ckEditors.forEach((editor) => {
                editor.style.setProperty('z-index', '100', 'important');
            });
            
            // STEP 2: Fix dropdowns with proper z-index and natural behavior
            const dropdowns = document.querySelectorAll('.ck-dropdown__panel');
            dropdowns.forEach((dropdown) => {
                // Ultra-high z-index to be above ck-content
                dropdown.style.setProperty('z-index', '2147483647', 'important');
                
                // Remove any forced display/visibility - let CKEditor control these naturally
                dropdown.style.removeProperty('display');
                dropdown.style.removeProperty('visibility');
                dropdown.style.removeProperty('opacity');
                
                // Remove any custom styling that might interfere
                dropdown.style.removeProperty('border');
                dropdown.style.removeProperty('background');
                dropdown.style.removeProperty('box-shadow');
                dropdown.style.removeProperty('border-radius');
                dropdown.style.removeProperty('width');
                dropdown.style.removeProperty('min-width');
                dropdown.style.removeProperty('max-width');
                
                // Remove selected item borders and highlighting ONLY for heading dropdown
                const isHeadingDropdown = dropdown.querySelector('.ck-heading-dropdown') || 
                                        dropdown.closest('.ck-heading-dropdown') ||
                                        dropdown.querySelector('[class*="heading"]') ||
                                        Array.from(dropdown.querySelectorAll('.ck-button')).some(btn => 
                                            btn.textContent && (btn.textContent.includes('Heading') || btn.textContent.includes('Paragraph'))
                                        );
                
                if (isHeadingDropdown) {
                    const dropdownItems = dropdown.querySelectorAll('.ck-button, button, [role="menuitem"]');
                    dropdownItems.forEach(item => {
                        // Remove borders but keep hover functionality
                        item.style.setProperty('border', 'none', 'important');
                        item.style.setProperty('outline', 'none', 'important');
                        item.style.setProperty('box-shadow', 'none', 'important');
                        
                        // Restore hover effects with CSS
                        item.style.setProperty('transition', 'background-color 0.2s ease', 'important');
                        
                        // Add hover event listeners for custom hover effect
                        item.addEventListener('mouseenter', function() {
                            this.style.setProperty('background-color', '#0076A8', 'important');
                            this.style.setProperty('color', 'white', 'important');
                        });
                        
                        item.addEventListener('mouseleave', function() {
                            this.style.removeProperty('background-color');
                            this.style.removeProperty('color');
                        });
                    });
                }

                // Add non-interfering click monitoring for cleanup
                if (!dropdown.hasAttribute('data-click-monitored')) {
                    dropdown.setAttribute('data-click-monitored', 'true');
                    
                    dropdown.addEventListener('click', function(e) {
                        const target = e.target;
                        
                        // Only clean up if clicking on actual menu items (not just anywhere in dropdown)
                        if (target.classList.contains('ck-button') || 
                            target.closest('.ck-button') || 
                            target.hasAttribute('role')) {
                            
                            // Give CKEditor time to process the command, but be conservative about cleanup
                            setTimeout(() => {
                                // Only clean up if we're absolutely sure the modal is closed
                                const modalStillOpen = document.body.classList.contains('modal-open');
                                if (!modalStillOpen) {
                                    // Wait a bit more before cleaning up to ensure modal is really closed
                                    setTimeout(() => {
                                        if (!document.body.classList.contains('modal-open')) {
                                            dropdown.style.removeProperty('z-index');
                                        }
                                    }, 300);
                                }
                            }, 200);
                        }
                    }, true); // Use capture phase to not interfere
                }
            });
            
            // STEP 3: Force layout recalculation
            document.body.offsetHeight;
        }
    }

    // ============================================================================
    // SECTION 1B: NON-MODAL DROPDOWN CLEAN STYLING (SEPARATE FROM MODAL LOGIC)
    // ============================================================================
    
    function applyCleanStylingToNonModalDropdowns() {
        // Only apply to non-modal contexts (when modal is NOT open)
        const isModalOpen = document.body.classList.contains('modal-open');
        
        if (!isModalOpen) {
            const dropdowns = document.querySelectorAll('.ck-dropdown__panel');
            dropdowns.forEach((dropdown) => {
                // Skip if this dropdown is inside a modal (even if modal is not currently open)
                const isInModal = dropdown.closest('.modal') !== null;
                if (isInModal) {
                    return; // Skip modal dropdowns entirely
                }
                
                // Apply clean styling ONLY to heading dropdowns in non-modal context
                const isHeadingDropdown = dropdown.querySelector('.ck-heading-dropdown') || 
                                        dropdown.closest('.ck-heading-dropdown') ||
                                        dropdown.querySelector('[class*="heading"]') ||
                                        Array.from(dropdown.querySelectorAll('.ck-button')).some(btn => 
                                            btn.textContent && (btn.textContent.includes('Heading') || btn.textContent.includes('Paragraph'))
                                        );
                
                if (isHeadingDropdown) {
                    const dropdownItems = dropdown.querySelectorAll('.ck-button, button, [role="menuitem"]');
                    dropdownItems.forEach(item => {
                        // Check if already processed to avoid duplicate event listeners
                        if (!item.hasAttribute('data-non-modal-styled')) {
                            item.setAttribute('data-non-modal-styled', 'true');
                            
                            // Remove borders but keep hover functionality
                            item.style.setProperty('border', 'none', 'important');
                            item.style.setProperty('outline', 'none', 'important');
                            item.style.setProperty('box-shadow', 'none', 'important');
                            
                            // Restore hover effects with CSS
                            item.style.setProperty('transition', 'background-color 0.2s ease', 'important');
                            
                            // Add hover event listeners for custom hover effect
                            item.addEventListener('mouseenter', function() {
                                this.style.setProperty('background-color', '#0076A8', 'important');
                                this.style.setProperty('color', 'white', 'important');
                            });
                            
                            item.addEventListener('mouseleave', function() {
                                this.style.removeProperty('background-color');
                                this.style.removeProperty('color');
                            });
                        }
                    });
                }
            });
        }
    }

    // ============================================================================
    // SECTION 2: DROPDOWN CLOSING FUNCTIONALITY
    // ============================================================================
    
    function closeCKEditorDropdowns() {
        // Find all open dropdowns
        const openDropdowns = document.querySelectorAll('.ck-dropdown.ck-dropdown_open');
        
        openDropdowns.forEach(dropdown => {
            // Remove the open class to close the dropdown
            dropdown.classList.remove('ck-dropdown_open');
            
            // Also hide the panel
            const panel = dropdown.querySelector('.ck-dropdown__panel');
            if (panel) {
                panel.classList.remove('ck-dropdown__panel_visible');
            }
        });
    }
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
        // Don't close if clicking on dropdown button or panel
        if (!e.target.closest('.ck-dropdown')) {
            closeCKEditorDropdowns();
        }
    });

    // ============================================================================
    // SECTION 3: TOOLTIP FIX FOR PERSISTENT TOOLTIP ISSUE
    // ============================================================================
    
    function setupTooltipFix() {
        // Forceful tooltip hiding function
        function forceHideTooltips() {
            document.querySelectorAll('.ck-tooltip').forEach(function(tooltip) {
                // Skip link popups and balloon panels
                // Use multiple methods to force hide
                tooltip.style.setProperty('display', 'none', 'important');
                tooltip.style.setProperty('opacity', '0', 'important');
                tooltip.style.setProperty('visibility', 'hidden', 'important');
                tooltip.style.setProperty('pointer-events', 'none', 'important');
                tooltip.style.setProperty('transform', 'scale(0)', 'important');
                
                // Also add a class for CSS targeting
                tooltip.classList.add('force-hidden');
            });
        }

        // Show/hide tooltips based on toolbar hover
        document.addEventListener('mouseover', function(e) {
            if (e.target.closest('.ck-toolbar, .ck-button, .ck-dropdown__button')) {
                // Mouse is over toolbar - show tooltips
                showTooltips();
            } else {
                // Mouse is outside toolbar - hide tooltips
                forceHideTooltips();
            }
        });

        // Also hide tooltips on click outside toolbar
        document.addEventListener('click', function(e) {
            if (!e.target.closest('.ck-toolbar, .ck-button, .ck-dropdown__button, .ck-balloon-panel')) {
                forceHideTooltips();
            }
        });

        // Show tooltips function
        function showTooltips() {
            document.querySelectorAll('.ck-tooltip').forEach(function(tooltip) {
                // Show tooltips when hovering over toolbar
                tooltip.style.removeProperty('display');
                tooltip.style.removeProperty('opacity');
                tooltip.style.removeProperty('visibility');
                tooltip.style.removeProperty('pointer-events');
                tooltip.style.removeProperty('transform');
                tooltip.classList.remove('force-hidden');
                
                // CRITICAL: Set ultra-high z-index to appear above link edit popups and balloon panels
                tooltip.style.setProperty('z-index', '2147483647', 'important');
            });
        }
    }

    // ============================================================================
    // SECTION 3B: TOOLTIP Z-INDEX ENFORCEMENT (FOR MODAL AND LINK POPUP CONTEXTS)
    // ============================================================================
    
    function enforceTooltipZIndex() {
        // Ensure all visible tooltips have ultra-high z-index, especially in modal contexts
        const tooltips = document.querySelectorAll('.ck-tooltip:not(.force-hidden)');
        tooltips.forEach(function(tooltip) {
            // Check if tooltip is currently visible (has some opacity or display)
            const computedStyle = window.getComputedStyle(tooltip);
            const isVisible = computedStyle.display !== 'none' && 
                             computedStyle.visibility !== 'hidden' && 
                             parseFloat(computedStyle.opacity) > 0;
            
            if (isVisible) {
                // Set ultra-high z-index to ensure tooltip appears above everything
                tooltip.style.setProperty('z-index', '2147483647', 'important');
            }
        });
    }

    // ============================================================================
    // SECTION 4: ENHANCED FEATURES (OPTIONAL - Can be enabled if needed)
    // ============================================================================
    
    let enhancedFeaturesEnabled = false; // Set to true to enable enhanced features
    
    function addEnhancedFeatures() {
        if (!enhancedFeaturesEnabled) return;
        
        // Add clipboard buttons and other enhanced features
        const ckEditors = document.querySelectorAll('.ck-editor');
        
        ckEditors.forEach(editor => {
            if (editor.hasAttribute('data-enhanced')) return;
            editor.setAttribute('data-enhanced', 'true');
            
            // Add clipboard buttons to toolbar
            const toolbar = editor.querySelector('.ck-toolbar');
            if (toolbar && !toolbar.querySelector('.clipboard-buttons')) {
                const clipboardGroup = document.createElement('div');
                clipboardGroup.className = 'clipboard-buttons';
                clipboardGroup.style.cssText = `
                    display: inline-flex;
                    margin-left: 8px;
                    border-left: 1px solid #ccc;
                    padding-left: 8px;
                `;
                
                // Copy button
                const copyBtn = document.createElement('button');
                copyBtn.innerHTML = '📋 Copy';
                copyBtn.className = 'ck-button clipboard-btn';
                copyBtn.onclick = () => copyContent(editor);
                
                // Paste button  
                const pasteBtn = document.createElement('button');
                pasteBtn.innerHTML = '📄 Paste';
                pasteBtn.className = 'ck-button clipboard-btn';
                pasteBtn.onclick = () => pasteContent(editor);
                
                clipboardGroup.appendChild(copyBtn);
                clipboardGroup.appendChild(pasteBtn);
                toolbar.appendChild(clipboardGroup);
            }
        });
    }
    
    function copyContent(editor) {
        const content = editor.querySelector('.ck-content');
        if (content) {
            navigator.clipboard.writeText(content.innerHTML);
        }
    }
    
    function pasteContent(editor) {
        const content = editor.querySelector('.ck-content');
        if (content) {
            navigator.clipboard.readText().then(text => {
                content.innerHTML = text;
            });
        }
    }

    // ============================================================================
    // SECTION 5: INITIALIZATION AND MONITORING
    // ============================================================================
    
    // Observer with multiple timing to ensure dropdowns are fixed
    const observer = new MutationObserver(function(mutations) {
        let shouldFix = false;
        
        mutations.forEach(function(mutation) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) {
                        if (node.classList && node.classList.contains('ck-dropdown__panel')) {
                            shouldFix = true;
                        }
                        const dropdowns = node.querySelectorAll && node.querySelectorAll('.ck-dropdown__panel');
                        if (dropdowns && dropdowns.length > 0) {
                            shouldFix = true;
                        }
                    }
                });
            }
        });
        
        if (shouldFix) {
            // Multiple timing attempts to ensure fixes stick
            fixModalDropdowns();
            setTimeout(fixModalDropdowns, 10);
            setTimeout(fixModalDropdowns, 50);
        }
        
        // Also check for new editors for enhanced features
        if (enhancedFeaturesEnabled) {
            setTimeout(addEnhancedFeatures, 100);
        }
    });
    
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
    });
    
    // Also run on resize events (like dev tools behavior)
    window.addEventListener('resize', fixModalDropdowns);
    window.addEventListener('resize', applyCleanStylingToNonModalDropdowns);
    
    // Initial run and periodic backup
    fixModalDropdowns();
    setInterval(fixModalDropdowns, 200);
    
    // Non-modal clean styling (separate timing to avoid conflicts)
    applyCleanStylingToNonModalDropdowns();
    setInterval(applyCleanStylingToNonModalDropdowns, 200);
    
    // Initialize tooltip fix (runs immediately and on DOM ready)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupTooltipFix);
    } else {
        setupTooltipFix();
    }
    
    // Enforce tooltip z-index continuously (especially important for modals and link popups)
    setInterval(enforceTooltipZIndex, 100); // Run frequently to catch new tooltips
    
    // Initialize enhanced features if enabled
    if (enhancedFeaturesEnabled) {
        setTimeout(addEnhancedFeatures, 100);
        setInterval(addEnhancedFeatures, 500);
    }
    
    // Export configuration for external access if needed
    window.CKEditorEnhancement = {
        enableEnhancedFeatures: function() {
            enhancedFeaturesEnabled = true;
            addEnhancedFeatures();
        },
        disableEnhancedFeatures: function() {
            enhancedFeaturesEnabled = false;
        }
    };

})();